import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AdminStatCard } from "./AdminStatCard";
import { toast } from "sonner";
import { Search, RefreshCw, Clock, CheckCircle, Play, AlertTriangle, Check, CalendarDays } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminRezervimet = ({ bookings, cars, onRefresh }) => {
  const [stats, setStats] = useState({ pending: 0, confirmed: 0, active: 0, today_active: 0, overdue: 0, completed: 0 });
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const res = await axios.get(`${API}/admin/reservations/stats`, { headers: { Authorization: `Bearer ${token}` } });
      setStats(res.data);
    } catch (e) { console.error(e); }
  };

  const getCarName = (carId) => {
    const car = cars.find(c => c.id === carId);
    return car ? `${car.brand} ${car.model}` : carId;
  };

  const today = new Date().toISOString().slice(0, 10);

  const isOverdue = (b) => {
    const rd = (b.return_date || "").slice(0, 10);
    return rd < today && b.status !== "cancelled" && b.status !== "completed";
  };

  const isTodayActive = (b) => {
    const pd = (b.pickup_date || "").slice(0, 10);
    const rd = (b.return_date || "").slice(0, 10);
    return pd <= today && today <= rd && (b.status === "confirmed" || b.status === "active" || b.status === "pending");
  };

  const filtered = bookings.filter(b => {
    // Special filters
    if (statusFilter === "overdue") {
      if (!isOverdue(b)) return false;
    } else if (statusFilter === "today_active") {
      if (!isTodayActive(b)) return false;
    } else if (statusFilter !== "all" && b.status !== statusFilter) {
      return false;
    }

    if (search) {
      const s = search.toLowerCase();
      return (b.customer_name || "").toLowerCase().includes(s) ||
             (b.customer_email || "").toLowerCase().includes(s) ||
             getCarName(b.car_id).toLowerCase().includes(s);
    }
    return true;
  });

  const statusColor = (s) => {
    const m = { pending: "bg-yellow-600/20 text-yellow-500", confirmed: "bg-blue-600/20 text-blue-500", active: "bg-green-600/20 text-green-500", completed: "bg-gray-600/20 text-gray-400", cancelled: "bg-red-600/20 text-red-500" };
    return m[s] || m.pending;
  };

  const statusLabel = (s) => {
    const m = { pending: "Në pritje", confirmed: "Konfirmuar", active: "Aktive", completed: "Përfunduar", cancelled: "Anulluar" };
    return m[s] || s;
  };

  const toggleFilter = (f) => setStatusFilter(statusFilter === f ? "all" : f);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-white">Rezervimet</h2>
      </div>

      <div className="bg-gradient-to-r from-zinc-800 to-zinc-900 rounded-xl p-5 mb-6 border border-yellow-600/20">
        <p className="text-yellow-500 text-xs font-semibold tracking-wider uppercase">Sistemi i Rezervimeve</p>
        <p className="text-gray-300 mt-1">Menaxho të gjitha rezervimet dhe disponueshmërinë</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
        <AdminStatCard value={stats.pending} label="Në pritje" color="yellow" icon={<Clock className="h-5 w-5" />} active={statusFilter === "pending"} onClick={() => toggleFilter("pending")} />
        <AdminStatCard value={stats.confirmed} label="Konfirmuar" color="blue" icon={<CheckCircle className="h-5 w-5" />} active={statusFilter === "confirmed"} onClick={() => toggleFilter("confirmed")} />
        <AdminStatCard value={stats.active} label="Aktive" color="green" icon={<Play className="h-5 w-5" />} active={statusFilter === "active"} onClick={() => toggleFilter("active")} />
        <AdminStatCard value={stats.today_active} label="Sot aktive" color="purple" icon={<CalendarDays className="h-5 w-5" />} active={statusFilter === "today_active"} onClick={() => toggleFilter("today_active")} />
        <AdminStatCard value={stats.overdue} label="Vonuar" color="orange" icon={<AlertTriangle className="h-5 w-5" />} active={statusFilter === "overdue"} onClick={() => toggleFilter("overdue")} />
        <AdminStatCard value={stats.completed} label="Përfunduar" color="gray" icon={<Check className="h-5 w-5" />} active={statusFilter === "completed"} onClick={() => toggleFilter("completed")} />
      </div>

      <div className="flex gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Kërko sipas klientit ose tabelës..."
            className="bg-zinc-800 border-gray-700 text-white pl-10"
            data-testid="reservations-search"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-zinc-800 border border-gray-700 text-white rounded-md px-3"
          data-testid="reservations-filter"
        >
          <option value="all">Të gjitha</option>
          <option value="pending">Në pritje</option>
          <option value="confirmed">Konfirmuar</option>
          <option value="active">Aktive</option>
          <option value="overdue">Vonuar</option>
          <option value="today_active">Sot aktive</option>
          <option value="completed">Përfunduar</option>
          <option value="cancelled">Anulluar</option>
        </select>
        <Button variant="outline" onClick={() => { onRefresh(); loadStats(); }} className="border-gray-700 text-gray-400">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-500">
          <CalendarDays className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p>Nuk ka rezervime</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((b) => {
            const overdue = isOverdue(b);
            return (
              <div key={b.id} className={`bg-zinc-900 border rounded-xl p-4 hover:border-gray-700 transition-colors ${overdue ? 'border-orange-600/40' : 'border-gray-800'}`} data-testid={`reservation-${b.id}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-white font-semibold">{b.customer_name}</p>
                    <p className="text-gray-400 text-sm">{getCarName(b.car_id)}</p>
                    <p className="text-gray-500 text-xs mt-1">{b.pickup_date?.slice(0,10)} → {b.return_date?.slice(0,10)}</p>
                  </div>
                  <div className="text-right">
                    <div className="flex flex-col items-end gap-1">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${statusColor(b.status)}`}>
                        {statusLabel(b.status)}
                      </span>
                      {overdue && (
                        <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-orange-600/20 text-orange-400">
                          Vonuar
                        </span>
                      )}
                    </div>
                    <p className="text-yellow-500 font-bold mt-2">€{b.total_price?.toFixed(2)}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
